import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer({
  isMobile,
  closeMobileMenu
}: {
  isMobile?: boolean;
  closeMobileMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="38395" className={`flex ${isMobile ? "flex-col gap-4" : "gap-1"}`}>
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className={`h-9 bg-muted/20 animate-pulse rounded-md ${isMobile ? "w-full" : "w-20"}`} />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col gap-6 p-6">
        {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border/10 pb-4 last:border-0">
            <Link to={item.href || "#"} className="text-lg font-serif font-bold uppercase tracking-widest text-foreground hover:text-primary transition-colors block" onClick={closeMobileMenu}>
              {item.label}
            </Link>
          </div>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-serif font-bold uppercase tracking-widest text-foreground/70 hover:text-foreground transition-colors py-2">
                {item.label}
                {}
                <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="global-navigation" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-muted py-3" : "bg-background py-6"}`}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        {}
        <Link to="/HeroesPage" className="flex items-center gap-2 z-50">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591123/img/webcraft_monogram_logo.png" alt="Geroi.eu Logo" className="h-8 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="17">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="md:hidden z-50">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-primary/10" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {}
        {isMobileMenuOpen && <div className="fixed inset-0 z-40 bg-background/95 backdrop-blur-xl md:hidden pt-24 animate-in fade-in slide-in-from-top-5 duration-300">
            <div className="container mx-auto px-4">
              <MenuProvider menu_id="17">
                <HeaderMenuConsumer isMobile={true} closeMobileMenu={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </div>
          </div>}
      </div>
    </header>;
}